import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Card, CardContent } from "@shared/ui/card";
import { useParams } from 'react-router-dom';
import { useGetPlanDetailByTab } from '../../../hooks';
import TimeOfUse from '../../../../rate/components/detail/tabs/overview/rateTypes/TimeOfUse';
import SlabBased from '../../../../rate/components/detail/tabs/overview/rateTypes/SlabBased';
import FlatRate from '../../../../rate/components/detail/tabs/overview/rateTypes/FlatRate';
import Seasonal from '../../../../rate/components/detail/tabs/overview/rateTypes/Seasonal';

const RatesIncludedTab = () => {
  const { id } = useParams();

  const { data: planDetail } = useGetPlanDetailByTab(id, "rates");



  const [expandedRates, setExpandedRates] = useState<number[]>([1]);

  const toggleRate = (id: number) => {
    setExpandedRates(prev => 
      prev.includes(id) ? prev.filter(rId => rId !== id) : [...prev, id]
    );
  };

  const SERVICE_COLORS: Record<string, string> = {
    gas: "bg-orange-500",
    water: "bg-blue-500",
    electricity: "bg-yellow-500",
    sewer: "bg-teal-500",
    "ev charging": "bg-green-500"
  };

  const ratesData = React.useMemo(() => {
    if (!planDetail?.result) return [];

    const ratesByService = planDetail.result.ratesByService || {};
    const conditionalRules = planDetail.result.conditionalRateRules || {};
    
    // Get all unique services
    const allServices = Array.from(new Set([
      ...Object.keys(ratesByService),
      ...Object.keys(conditionalRules)
    ]));

    return allServices.map(service => {
      const rates = [];
      const condRule = conditionalRules[service];
      
      if (condRule?.enabled) {
        if (condRule.belowRate) {
          rates.push({
            id: condRule.belowRate.rateId || `below-${service}`,
            name: condRule.belowRate.rateName,
            type: condRule.belowRate.rateTypeDisplay || "Flat Rate",
            code: condRule.belowRate.rateCode || "N/A",
            description: condRule.belowRate.description || `Applies when consumption is below ${condRule.threshold} ${condRule.thresholdUnit}`,
            isActive: condRule.belowRate.statusDisplay?.toUpperCase() === "ACTIVE",
            structure: condRule.belowRate.rateStructure || condRule.rateStructure,
            charges: [],
            originalData: condRule.belowRate
          });
        }
        if (condRule.aboveRate) {
          rates.push({
            id: condRule.aboveRate.rateId || `above-${service}`,
            name: condRule.aboveRate.rateName,
            type: condRule.aboveRate.rateTypeDisplay || "Flat Rate",
            code: condRule.aboveRate.rateCode || "N/A",
            description: condRule.aboveRate.description || `Applies when consumption is above ${condRule.threshold} ${condRule.thresholdUnit}`,
            isActive: condRule.aboveRate.statusDisplay?.toUpperCase() === "ACTIVE",
            structure: condRule.aboveRate.rateStructure||condRule.rateStructure,
            charges: [],
            originalData: condRule.aboveRate
          });
        }
      } else if (ratesByService[service]) {
        const rate = ratesByService[service];
        rates.push({
          id: rate.rateId || `rate-${service}`,
          name: rate.rateName,
          type: rate.rateTypeDisplay || "Flat Rate",
          code: rate.rateCode || "N/A",
          description: rate.description || "Standard rate",
          isActive: rate.statusDisplay?.toUpperCase() === "ACTIVE",
          structure: rate.rateStructure, // Could map from rate.rateStructure if available
          originalData: rate,
          charges: [
            ...(rate.fixedCharges || []).map((fc: any) => ({
              label: fc.name,
              value: `$${fc.amount}`
            })),
            ...(rate.taxes || []).map((tax: any) => ({
              label: tax.name || "Tax",
              value: `${tax.rate}%` // simplified
            }))
          ]
        });
      }

      return {
        service: service.charAt(0).toUpperCase() + service.slice(1),
        serviceColor: SERVICE_COLORS[service.toLowerCase()] || "bg-purple-500",
        ratesCount: `${rates.length} rate${rates.length !== 1 ? 's' : ''}`,
        rates
      };
    });
  }, [planDetail]);
console.log('rates daata:',ratesData)

  return (
    <div className="space-y-2">
      {ratesData.map((serviceGroup, index) => (
        <Card key={index} className="shadow-sm border border-gray-200">
          <CardContent className="">
            {/* Service Header */}
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-2 h-2 rounded-full ${serviceGroup.serviceColor}`}></span>
              <h3 className="text-[15px] font-bold text-gray-900">{serviceGroup.service}</h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-50 text-gray-600 border border-gray-200">
                {serviceGroup.ratesCount}
              </span>
            </div>

            {/* Rates List */}
            <div className="space-y-3">
              {serviceGroup.rates.map(rate => {
                const isExpanded = expandedRates.includes(rate.id);
                
                return (
                  <div key={rate.id} className="border border-gray-900 rounded-lg overflow-hidden border-opacity-10">
                    <div className="border border-gray-900 rounded-lg overflow-hidden">
                      {/* Rate Header */}
                      <div 
                        className="p-4 cursor-pointer bg-white hover:bg-gray-50 flex items-start gap-3 transition-colors"
                        onClick={() => toggleRate(rate.id)}
                      >
                        <button className="mt-1 text-gray-400 shrink-0">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="text-sm font-bold text-gray-900">{rate.name}</h4>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200 tracking-wide">
                              {rate.type}
                            </span>
                            <span className="text-[10px] font-bold text-gray-400 tracking-widest">
                              {rate.code}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 max-w-4xl">{rate.description}</p>
                        </div>

                        <div className="flex items-center gap-3 ml-auto pl-4 shrink-0">
                          {rate.isActive && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200 tracking-wider">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                              Active
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-gray-900 border-opacity-10 p-5 bg-white">
                          <div className=" gap-x-12 gap-y-4">
                            {/* Rate Structure Column */}
                            <div>
                              <div className="w-full">
                                {(() => {
                                  const typeDisplay = rate.type;
                                  if (typeDisplay === 'Time of Use' || rate.originalData?.basisType == "Time Of Use" ) {
                                    return <TimeOfUse slots={rate.structure||rate.originalData?.rateStructure || []} />;
                                  } else if (typeDisplay === 'Slab Based' || rate.originalData?.basisType == "Slab" || rate.originalData?.basisType == "Tiered") {
                                    return <SlabBased slabs={rate.structure||rate.originalData?.rateStructure || []} variant="bars" />;
                                  } else if (typeDisplay === 'Seasonal' || rate.originalData?.basisType == "Seasonal") {
                                    return <Seasonal seasons={rate.structure||rate.originalData?.rateStructure || []} />;
                                  } else {
                                    return <FlatRate data={rate.structure||rate.originalData?.rateStructure || []} />;
                                  }
                                })()}
                              </div>
                            </div>

                            {/* Charges Column */}
                            
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default RatesIncludedTab;
